<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .dashboard-container
    web-page-header(title="Marketplace" subtitle="Discover and collect amazing stickers from creators around the world")

    //- Search & Filter Section
    .search-section
      .search-wrapper
        .search-input-container
          img(src="@/assets/icons/searchIcon.svg" alt="Search").search-icon
          input(
            v-model="state.searchTerm"
            placeholder="Search stickers..."
            @input="debouncedSearch"
            type="text"
          ).search-input
          button(
            v-if="state.searchTerm"
            @click="clearSearchAndRefresh"
            type="button"
          ).clear-button
            img(src="@/assets/icons/close.svg" alt="Clear")
        
        button(@click="openFilterModal" type="button").filter-button
          img(src="@/assets/icons/filterIcon.svg" alt="Filter").filter-icon
          span Filter
          .filter-count(v-if="activeFilterCount > 0") {{ activeFilterCount }}

    //- Active Filters Display
    .active-filters(v-if="state.activeFilters.length > 1")
      .filter-tags
        button(
          v-for="filter in displayActiveFilters"
          :key="filter"
          @click="removeFilter(filter)"
          type="button"
        ).filter-tag
          span {{ filter }}
          img(src="@/assets/icons/close.svg" alt="Remove")
        button(
          @click="clearFilters"
          type="button"
        ).clear-all-filters Clear all

    //- No Results State
    .no-results(v-if="state.stickers.length === 0 && !loading")
      .no-results-icon 🔍
      h3.no-results-title No stickers found
      p.no-results-text(v-if="state.searchTerm") Try a different search term or clear your filters.
      p.no-results-text(v-else) Try clearing your filters.
      button(@click="clearSearch" type="button").clear-search-button Clear Search & Filters

    //- Loading State
    web-page-skeleton(v-if="loading" :sections="1" :cardsPerSection="20" :showHeader="false" :showSectionTitles="false")

    //- Stickers Grid
    .stickers-grid-wrapper(v-if="!loading")
      stickers-grid-template(:stickers="state.filteredStickers")

    //- Pagination
    .pagination-wrapper(v-if="state.stickers.length > 0 && !loading")
      .pagination-controls
        button(
          @click="handleCurrentChange(state.currentPage - 1)"
          :disabled="state.currentPage === 1"
          type="button"
        ).pagination-button.prev
          img(src="@/assets/icons/chevron-right.svg" alt="Previous" style="transform: rotate(180deg);")
          span Previous
        
        .page-indicator
          span.page-current {{ state.currentPage }}
          span.page-separator /
          span.page-total {{ totalPages }}
        
        button(
          @click="handleCurrentChange(state.currentPage + 1)"
          :disabled="state.filteredStickers.length < 20"
          type="button"
        ).pagination-button.next
          span Next
          img(src="@/assets/icons/chevron-right.svg" alt="Next")

  //- Filter Modal
  el-dialog(
    v-model="state.modalVisible"
    title="Filter Stickers"
    width="400px"
    @close="resetFilters"
  ).filter-modal
    .filter-options
      .filter-option
        el-checkbox(v-model="state.freeFilter")
        label Free Stickers
      .filter-option
        el-checkbox(v-model="state.paidFilter")
        label Paid Stickers
      .filter-option
        el-checkbox(v-model="state.campaignFilter")
        label Campaigns
      .filter-option
        el-checkbox(v-model="state.ticketFilter")
        label Tickets
      .filter-option
        el-checkbox(v-model="state.sweepstakesFilter")
        label Sweepstakes
    
    template(#footer)
      .filter-modal-footer
        button(@click="state.modalVisible = false" type="button").cancel-button Cancel
        button(@click="applyFilters" type="button").apply-button Apply Filters
</template>

<script lang="ts">
import {
  defineComponent, onMounted, reactive, ref, computed,
} from 'vue';
import { useStore } from 'vuex';
import StickersGridTemplate from '../sticker/StickersGridTemplate.vue';
import WebPageSkeleton from '../components/WebPageSkeleton.vue';
import gqlRequest from './gql-request';
import WebAppLayout from './WebAppLayout.vue';
import WebPageHeader from '../components/WebPageHeader.vue';

export default defineComponent({
  name: 'WebAppDashboard',
  components: { WebAppLayout, WebPageHeader, StickersGridTemplate, WebPageSkeleton },
  setup() {
    const {
      route,
      router,
      loading,
      getPublishedStickers,
      getPublishedPublicStickers,
    } = gqlRequest();
    const store = useStore();
    const state = reactive({
      stickers: [] as any[],
      filteredStickers: [] as any[],
      searchTerm: '',
      modalVisible: false,
      freeFilter: false,
      paidFilter: false,
      campaignFilter: false,
      ticketFilter: false,
      sweepstakesFilter: false,
      currentPage: 1,
      totalStickers: 0,
      activeFilters: [] as string[],
    });

    const searchTimeout = ref<number | null>(null);
    const hasFetchedPublishedStickers = computed(() => store.getters['sticker/HAS_FETCHED_PUBLISHED_STICKERS']);

    function debouncedSearch() {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }

      searchTimeout.value = window.setTimeout(() => {
        searchStickers();
      }, 500);
    }

    async function getStickers() {
      state.stickers = [];
      state.filteredStickers = [];
      loading.value = true;

      try {
        const pageSize = 20;
        const skip = (state.currentPage - 1) * pageSize;
        const activeFilters = [...state.activeFilters];
        if (!activeFilters.includes('Active')) {
          activeFilters.push('Active');
        }

        let stickers;
        if (!hasFetchedPublishedStickers.value
              || state.searchTerm
              || state.activeFilters.length > 1
              || state.currentPage > 1) {
          if (store.getters['users/me'] != null) {
            stickers = await getPublishedStickers(
              pageSize, skip, activeFilters, state.searchTerm);
          } else {
            stickers = await getPublishedPublicStickers(
              pageSize, skip, activeFilters, state.searchTerm);
          }
          if (!state.searchTerm && state.activeFilters.length <= 1 && state.currentPage === 1) {
            store.dispatch('sticker/SET_PUBLISHED_STICKERS', stickers);
          }
        } else {
          stickers = store.getters['sticker/GET_PUBLISHED_STICKERS'];
        }

        state.stickers = stickers;
        state.filteredStickers = stickers;

        updateUrlParams();
      } catch (error) {
        console.error('Error fetching stickers:', error);
      } finally {
        loading.value = false;
      }
    }

    function handleCurrentChange(newPage: number) {
      state.currentPage = newPage;
      getStickers();
    }

    async function searchStickers() {
      state.currentPage = 1;
      await getStickers();
    }

    function openFilterModal() {
      state.freeFilter = state.activeFilters.includes('Free');
      state.paidFilter = state.activeFilters.includes('Paid');
      state.campaignFilter = state.activeFilters.includes('Campaign');
      state.ticketFilter = state.activeFilters.includes('Ticket');
      state.sweepstakesFilter = state.activeFilters.includes('Sweepstakes');

      state.modalVisible = true;
    }

    function resetFilters() {
      state.freeFilter = state.activeFilters.includes('Free');
      state.paidFilter = state.activeFilters.includes('Paid');
      state.campaignFilter = state.activeFilters.includes('Campaign');
      state.ticketFilter = state.activeFilters.includes('Ticket');
      state.sweepstakesFilter = state.activeFilters.includes('Sweepstakes');
    }

    async function applyFilters() {
      state.activeFilters = ['Active'];

      if (state.campaignFilter) {
        state.activeFilters.push('Campaign');
      }
      if (state.paidFilter) {
        state.activeFilters.push('Paid');
      }
      if (state.freeFilter) {
        state.activeFilters.push('Free');
      }
      if (state.ticketFilter) {
        state.activeFilters.push('Ticket');
      }
      if (state.sweepstakesFilter) {
        state.activeFilters.push('Sweepstakes');
      }

      state.currentPage = 1;
      await getStickers();
      state.modalVisible = false;
    }

    async function clearSearch() {
      state.searchTerm = '';
      state.freeFilter = false;
      state.paidFilter = false;
      state.campaignFilter = false;
      state.ticketFilter = false;
      state.sweepstakesFilter = false;
      state.activeFilters = ['Active'];
      state.currentPage = 1;

      await getStickers();
    }

    function clearSearchAndRefresh() {
      if (!state.searchTerm.length) return;

      state.searchTerm = '';
      searchStickers();
    }

    function clearFilters() {
      state.activeFilters = ['Active'];
      resetFilters();
      searchStickers();
    }

    function removeFilter(filter: string) {
      if (filter === 'Active') return;

      const index = state.activeFilters.indexOf(filter);
      if (index !== -1) {
        state.activeFilters.splice(index, 1);

        if (filter === 'Free') state.freeFilter = false;
        if (filter === 'Paid') state.paidFilter = false;
        if (filter === 'Campaign') state.campaignFilter = false;
        if (filter === 'Ticket') state.ticketFilter = false;
        if (filter === 'Sweepstakes') state.sweepstakesFilter = false;

        searchStickers();
      }
    }

    function updateUrlParams() {
      const query: { [key: string]: any } = {};

      if (state.searchTerm) query.search = state.searchTerm;
      if (state.activeFilters.length > 0) {
        const displayFilters = state.activeFilters.filter((filter) => filter !== 'Active');
        if (displayFilters.length > 0) {
          query.filters = displayFilters.join(',');
        }
      }
      if (state.currentPage > 1) query.page = state.currentPage;

      router.replace({ query }).catch(() => {});
    }

    onMounted(async () => {
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.has('search')) {
        state.searchTerm = urlParams.get('search') || '';
      }

      if (urlParams.has('filters')) {
        const filters = (urlParams.get('filters') || '').split(',');
        state.activeFilters = ['Active', ...filters];

        state.freeFilter = filters.includes('Free');
        state.paidFilter = filters.includes('Paid');
        state.campaignFilter = filters.includes('Campaign');
        state.ticketFilter = filters.includes('Ticket');
        state.sweepstakesFilter = filters.includes('Sweepstakes');
      } else {
        state.activeFilters = ['Active'];
      }

      if (urlParams.has('page')) {
        const page = parseInt(urlParams.get('page') || '1', 10);
        if (!isNaN(page) && page > 0) {
          state.currentPage = page;
        }
      }

      if (urlParams.has('size')) {
        const size = parseInt(urlParams.get('size') || '20', 10);
        if (!Number.isNaN(size) && [5, 10, 20, 50].includes(size)) {
          // Page size is now fixed at 20, so we ignore this parameter
        }
      }

      await getStickers();
    });

    const activeFilterCount = computed(() => {
      return state.activeFilters.filter((f) => f !== 'Active').length;
    });

    const displayActiveFilters = computed(() => {
      return state.activeFilters.filter((f) => f !== 'Active');
    });

    const totalPages = computed(() => {
      return Math.max(1, state.currentPage + (state.filteredStickers.length >= 20 ? 1 : 0));
    });

    return {
      route,
      router,
      loading,
      state,
      searchStickers,
      openFilterModal,
      applyFilters,
      resetFilters,
      handleCurrentChange,
      clearSearch,
      clearSearchAndRefresh,
      clearFilters,
      removeFilter,
      updateUrlParams,
      debouncedSearch,
      activeFilterCount,
      displayActiveFilters,
      totalPages,
    };
  },
});
</script>

<style lang="scss" scoped>
// Main Container
.dashboard-container {
  padding: 0 !important;
  min-height: 100vh;
  background: #ffffff;
  max-width: 1200px;

  :deep(.web-page-skeleton) {
    max-width: 100%;
    margin: 0;
  }
}

// Search Section
.search-section {
  padding: 24px 24px 0 24px;
  margin-bottom: 24px;
  
  @media (min-width: 768px) {
    padding: 32px 40px 0 40px;
    margin-bottom: 32px;
  }
  
  @media (min-width: 1024px) {
    padding: 40px 64px 0 64px;
  }
}

.search-wrapper {
  display: flex;
  gap: 12px;
  align-items: center;
  max-width: 100%;
  
  @media (min-width: 768px) {
    gap: 16px;
  }
}

.search-input-container {
  position: relative;
  flex: 1;
  max-width: 600px;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  opacity: 0.4;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 14px 48px 14px 48px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  color: #1a1a1a;
  background: #ffffff;
  transition: all 0.2s;
  
  &::placeholder {
    color: #9ca3af;
  }
  
  &:focus {
    outline: none;
    border-color: #0d9488;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }
}

.clear-button {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background 0.2s;
  
  img {
    width: 16px;
    height: 16px;
    opacity: 0.5;
  }
  
  &:hover {
    background: #f3f4f6;
    
    img {
      opacity: 0.8;
    }
  }
}

.filter-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  border: 2px solid #e5e7eb;
  background: #ffffff;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  white-space: nowrap;
  
  .filter-icon {
    width: 18px;
    height: 18px;
    opacity: 0.6;
  }
  
  &:hover {
    border-color: #0d9488;
    background: #f0fdfa;
    
    .filter-icon {
      opacity: 1;
    }
  }
  
  .filter-count {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #0d9488;
    color: white;
    font-size: 11px;
    font-weight: 700;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
  }
}

// Active Filters
.active-filters {
  padding: 0 24px 16px 24px;
  
  @media (min-width: 768px) {
    padding: 0 40px 24px 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 64px 24px 64px;
  }
}

.filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  color: #0f766e;
  cursor: pointer;
  transition: all 0.2s;
  
  img {
    width: 12px;
    height: 12px;
    opacity: 0.6;
  }
  
  &:hover {
    background: #ccfbf1;
    border-color: #5eead4;
    
    img {
      opacity: 1;
    }
  }
}

.clear-all-filters {
  padding: 6px 12px;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: #1a1a1a;
  }
}

// No Results
.no-results {
  padding: 80px 24px;
  text-align: center;
  
  @media (min-width: 768px) {
    padding: 100px 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 120px 64px;
  }
}

.no-results-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
}

.no-results-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.no-results-text {
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 24px 0;
  line-height: 1.6;
}

.clear-search-button {
  padding: 12px 24px;
  background: #0d9488;
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #0f766e;
    transform: scale(1.02);
  }
}

// Loading State
.loading-container {
  padding: 80px 24px;
  text-align: center;
  
  @media (min-width: 768px) {
    padding: 100px 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 120px 64px;
  }
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #0d9488;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

// Stickers Grid
.stickers-grid-wrapper {
  padding: 0 24px;
  margin-bottom: 40px;
  
  @media (min-width: 768px) {
    padding: 0 40px;
    margin-bottom: 48px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 64px;
    margin-bottom: 56px;
  }
}

// Pagination
.pagination-wrapper {
  padding: 0 24px 64px 24px;
  display: flex;
  justify-content: center;
  
  @media (min-width: 768px) {
    padding: 0 40px 80px 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 64px 96px 64px;
  }
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: nowrap;
  
  @media (max-width: 768px) {
    gap: 12px;
  }
}

.pagination-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 2px solid #e5e7eb;
  background: white;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
  
  img {
    width: 16px;
    height: 16px;
    opacity: 0.6;
    flex-shrink: 0;
  }
  
  &:hover:not(:disabled) {
    border-color: #0d9488;
    background: #f0fdfa;
    
    img {
      opacity: 1;
    }
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  @media (max-width: 640px) {
    padding: 8px 16px;
    font-size: 13px;
    
    span {
      display: none;
    }
    
    gap: 0;
  }
}

.page-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f9fafb;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  
  .page-current {
    color: #0d9488;
  }
  
  .page-separator {
    color: #d1d5db;
  }
  
  .page-total {
    color: #6b7280;
  }
  
  @media (max-width: 640px) {
    padding: 8px 12px;
    font-size: 13px;
    gap: 6px;
  }
}

// Filter Modal
:deep(.filter-modal) {
  .el-dialog__header {
    padding: 24px 24px 16px 24px;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .el-dialog__title {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
  }
  
  .el-dialog__body {
    padding: 24px;
  }
  
  .el-dialog__footer {
    padding: 0;
  }
}

.filter-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  transition: background 0.2s;
  cursor: pointer;
  
  &:hover {
    background: #f9fafb;
  }
  
  label {
    font-size: 15px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    user-select: none;
  }
}

.filter-modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 24px 24px 24px;
  border-top: 1px solid #e5e7eb;
}

.cancel-button {
  flex: 1;
  padding: 12px 24px;
  border: 2px solid #e5e7eb;
  background: white;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
}

.apply-button {
  flex: 1;
  padding: 12px 24px;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3);
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 6px 16px rgba(13, 148, 136, 0.4);
  }
  
  &:active {
    transform: scale(0.98);
  }
}

// Responsive adjustments
@media screen and (max-width: 768px) {
  .search-wrapper {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input-container {
    max-width: 100%;
  }
  
  .filter-button {
    width: 100%;
    justify-content: center;
  }
}

@media screen and (max-width: 500px) {
  :deep(.filter-modal) {
    width: 95% !important;
  }
}
</style>
